import { Sparkles } from 'lucide-react'
import { useMemo } from 'react'
import { Link, Outlet } from 'react-router'
import { DevelopedBy } from '@/components/brand/developed-by'
import { Logo } from '@/components/brand/logo'
import { todayIn, type DateOnly } from '@/domain/dates'
import { buildCalendar, suggestBridges } from '@/domain/vacations'
import { formatPeriod } from '@/lib/format'

export function AuthLayout() {
  return (
    <div className="grid min-h-dvh lg:grid-cols-[1fr_1.1fr]">
      <div className="flex flex-col px-6 py-8 sm:px-12">
        <Link to="/" aria-label="Nolens Vacations — página inicial" className="self-start">
          <Logo />
        </Link>
        <div className="flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-sm">
            <Outlet />
          </div>
        </div>
        <p className="text-xs text-fg-subtle">
          © {new Date().getFullYear()} Nolens · Tudo ligado. Nada esquecido. · <DevelopedBy />
        </p>
      </div>
      <div className="relative hidden overflow-hidden border-l border-border bg-surface lg:block">
        <BridgesTeaser />
      </div>
    </div>
  )
}

/**
 * Reciprocity: before asking for anything, give something useful — this year's best
 * "pontes", computed live. Contrast effect: each one shows days spent vs. days of rest.
 */
function BridgesTeaser() {
  const today = todayIn()
  const bridges = useMemo(() => {
    const cal = buildCalendar()
    const year = Number(today.slice(0, 4))
    return suggestBridges(cal, today, `${year + 1}-12-31` as DateOnly)
      .filter((b) => b.ratio >= 2)
      .slice(0, 5)
  }, [today])
  const spent = bridges.reduce((s, b) => s + b.workingDays, 0)
  const rest = bridges.reduce((s, b) => s + b.restDays, 0)

  return (
    <div className="flex h-full flex-col justify-center gap-10 px-16">
      <div>
        <p className="text-3xl leading-tight font-semibold tracking-tight">
          Férias bem planeadas.
          <br />
          <span className="text-fg-muted">Equipa sempre coberta.</span>
        </p>
        <p className="mt-4 max-w-md text-base text-fg-muted">Pedidos, aprovações, saldos e calendário da equipa num só sítio, com as regras do Código do Trabalho já aplicadas.</p>
      </div>
      {bridges.length > 0 && (
        <section aria-labelledby="teaser" className="max-w-md rounded-xl border border-border bg-bg p-5 shadow-xs">
          <h2 id="teaser" className="flex items-center gap-2 text-sm font-semibold">
            <Sparkles className="size-4 text-accent" aria-hidden /> As melhores pontes que vêm aí
          </h2>
          <p className="mt-1 text-sm text-fg-muted">
            Com <strong className="text-fg">{spent} dias</strong> de férias, descansa <strong className="text-accent">{rest} dias</strong>.
          </p>
          <ul className="mt-4 grid gap-2">
            {bridges.map((b) => (
              <li key={b.start} className="flex items-center gap-3 text-sm">
                <span className="w-28 shrink-0 text-fg-muted tabular">{formatPeriod(b.restFrom, b.restTo)}</span>
                <span className="min-w-0 flex-1 truncate">{b.holidays[0]}</span>
                <span className="shrink-0 text-xs text-fg-subtle tabular">
                  {b.workingDays} → <strong className="text-fg">{b.restDays} dias</strong>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
